// src/utils/contractFellThrough.js
// Contract Fell Through and Restore This Contract, as pure planning.
//
// When a buyer backs out of a listing, the buyer's whole contract is saved to
// deals/{dealId}/contractHistory/{historyId} and then cleared from the deal so
// the house can go back on the market. This file decides exactly what is saved,
// what is cleared and what stays. It has no Firebase and no React imports, so
// every rule is unit tested in src/utils/__tests__/contractFellThrough.test.js.
// Reading and writing live in src/utils/contractHistoryStore.js.
//
// Spec: docs/superpowers/specs/2026-09-14-contract-fell-through-design.md

import { isBuyerSide } from "./taskSections";
import { stageEntryPatch, todayYMD as prepTodayYMD } from "./listingPrep";

export const FELL_THROUGH_FROM_STAGES = ["Under Contract", "Pending"];
// Where a listing can go when its contract falls through. On Hold joined the
// list on 09/16/2026 (Henry): parking a listing is the same event as putting it
// back on the market, so every doorway asks "Did the Contract Fall Through?" for
// a move to On Hold exactly as it does for Active, Coming Soon and Pre-Market.
// On Hold is only ever a destination: it is not in FELL_THROUGH_FROM_STAGES (a
// parked listing has no contract to lose) and not in CONTRACT_TASK_STAGES (an
// On Hold task is not a contract task, and a parked listing can still restore).
export const BACK_TO_STAGES = ["Active", "Coming Soon", "Pre-Market", "On Hold"];
export const FELL_THROUGH_REASONS = [
  "Inspection",
  "Financing",
  "Appraisal",
  "Buyer Changed Their Mind",
  "Other",
];
export const CONTRACT_TASK_STAGES = ["Under Contract", "Pending", "Closed"];

// Markers, kept as plain strings so a plan is ordinary data a test can compare.
//   DELETE_FIELD: remove this field from the deal. The store turns it into deleteField().
//   MISSING: inside a saved contract, "the deal did not have this field", so a
//   restore removes the field again instead of inventing a value.
export const DELETE_FIELD = "__tes_delete_field__";
export const MISSING = "__tes_missing__";

export const RESTORE_BLOCKED_MESSAGE =
  "This contract can't be restored because a newer buyer's details were entered after it fell through.";

// One sentence per canRestore reason, so nobody is told a newer buyer's details
// were entered when the real reason is something else. "under-contract" covers
// Under Contract and Pending: the listing is carrying a contract again, and that
// one has to be dealt with before an old one comes back. "closed" is a sold
// house, which is not under contract and must never be told to go back on the
// market (09/16/2026 review).
const RESTORE_BLOCKED_MESSAGES = {
  "newer-contract": RESTORE_BLOCKED_MESSAGE,
  "under-contract":
    "This contract can't be restored while the listing is under contract again. Move the listing back on the market first.",
  closed: "This contract can't be restored because the listing has already closed.",
  "wrong-side": "This contract can't be restored because this file is no longer a seller's listing.",
  "already-restored": "This contract has already been restored, so there is nothing left to put back.",
};

// Anything unexpected falls back to the plain sentence rather than blaming a
// buyer who may not exist.
const RESTORE_BLOCKED_FALLBACK = "This contract can't be restored right now.";

export function restoreBlockedMessage(reason) {
  const message = RESTORE_BLOCKED_MESSAGES[reason];
  return typeof message === "string" ? message : RESTORE_BLOCKED_FALLBACK;
}

// Where a listing landed, worded for the Contract Fell Through and Contract
// Restored alerts: "back in Active", "back in Pending". A listing parked On Hold
// reads "now on hold", because "back in On Hold" reads oddly and the listing may
// never have been on hold before (09/16/2026 review). A blank stage gives "" so
// the caller can leave the phrase out.
export function stageArrivalPhrase(stage) {
  const stageText = String(stage || "").trim();
  if (!stageText) return "";
  if (stageText.toLowerCase() === "on hold") return "now on hold";
  return `back in ${stageText}`;
}

// ---- The fields a contract owns (spec: "What gets saved and cleared") -------

export const MILESTONE_FIELDS = [
  "offerPrice",
  "earnestMoney",
  "earnestMoneyReceivedDate",
  "finalAcceptanceDate",
  "inspection",
  "buyerInspection",
  "inspectionDeadline",
  "recissionPeriod", // misspelled in the app; this is the name the app reads
  "rescissionDeadline",
  "financingType",
  "financingDeadline",
  "appraisal",
  "appraisalComplete",
  "preSignDate",
  "finalWalkthrough",
  "closingdate",
  // Henry 10/06/2026: a purchase agreement term the Estimate of Proceeds follows (netSheet.js dealFigures).
  "sellerPaidClosingCosts",
];

export const TITLE_FIELDS = [
  "titleCompany",
  "titleCloserName",
  "titleCloserEmail",
  "titleCloserPhone",
  "otherTitleCompany",
  "otherTitleCloserName",
  "otherTitleCloserEmail",
  "otherTitleCloserPhone",
];

// Commission dollars and that contract's split numbers. Anyone who fails
// canSeeCommissions(user) (src/utils/roles.js) must never see these values.
export const COMMISSION_FIELDS = [
  "totalCommissionamount",
  "agentCommissionamount",
  "teamleadCommissionamount",
  "leadsourceCommissionamount",
  "bankcommission",
  "takehomecommissionamount",
  "thirdpartyagentcommissionamount",
  "agentCommission",
  "fixedagentcommission",
  "thirdpartyagentCommission",
  "flatfee",
  "flatfeeamount",
  "teamleaddiscount",
  "agentdiscount",
  "buyersidecommissionrate",
  "sellersidecommissionrate",
  "teamleadcommissionrate",
  "brokeradminfee",
  // The Waived switch (Henry 2026-09-18 evening): a new contract starts with the fee not waived.
  "brokeradminfeeWaived",
  "brokeradminfeeWaivedAt",
  "brokeradminfeeWaivedBy",
  "brokeradminfeeBeforeWaive",
];

// Who is on the deal: the team lead, whether it is an in-house referral, and the
// outside referral agent shown as the deal's Agent. These belong to the listing,
// not the buyer's contract, so a fall-through never clears them: wiping them took
// the team lead off the deal and sent the file to the wrong folder (Henry,
// 10/06/2026). agentData and agentName were already kept.
export const DEAL_PEOPLE_FIELDS = ["assignedteamlead", "teamleadName", "inhousereferral", "thirdpartyagentName"];

// Removed outright, so the next contract is reviewed by accounting fresh and an
// Active listing never sits in Needs Review with blank numbers.
export const ACCOUNTING_REMOVED_FIELDS = [
  "accountingStatus",
  "accountingReviewedBy",
  "accountingReviewedAt",
  "accountingFlagNote",
  "accountingFlaggedBy",
  "accountingFlaggedAt",
];

// awaitingClientCare: a Team Lead's own turn-in waiting on Client Care's Pending
// Approval list (Henry, 2026-09-19; src/utils/clientCareTurnIn.js). With the
// contract gone there is nothing left for Client Care to process, so it clears
// with the contract (a stale true would also put the next contract on the list,
// since Client Care's own turn-ins set no flag), and Restore puts it back.
export const ACCOUNTING_FIELDS = [
  ...ACCOUNTING_REMOVED_FIELDS,
  "requestType",
  "approvedby",
  "awaitingClientCare",
];

export const EMAIL_AND_DATE_FIELDS = ["underContractDate", "acceptanceEmails", "transactionEmails"];

export const CONTRACT_FIELDS = [
  ...MILESTONE_FIELDS,
  ...TITLE_FIELDS,
  ...COMMISSION_FIELDS,
  ...ACCOUNTING_FIELDS,
  ...EMAIL_AND_DATE_FIELDS,
];

// The value a brand-new contract holds in each field. Sources, in order:
//   1. src/utils/buildTurnInSeed.js, where the Sale Turn In seed sets the field
//      (except offerPrice and earnestMoney, see group 2)
//   2. otherwise the listing-details.jsx form defaults (defaultSellerFormData;
//      finalAcceptanceDate is only in defaultBuyerFormData)
//   3. title fields are in neither: the turn-in form (add-deal.jsx step1Data)
//      starts them at "" and hero.jsx saves them as `|| ""`
//   4. accounting, request, date and email fields: exactly what the spec says
// acceptanceEmails and transactionEmails are handled in clearedValueFor so each
// call returns its own new {}.
const CLEARED_VALUES = {
  // 1. buildTurnInSeed.js step1
  inspection: "",
  inspectionDeadline: "",
  financingType: "",
  financingDeadline: "",
  closingdate: "", // blank, NEVER a guessed date
  // 2. listing-details.jsx form defaults
  // never 0: several screens render deal.offerPrice && ..., which prints a bare 0
  offerPrice: "",
  earnestMoney: "",
  earnestMoneyReceivedDate: "",
  finalAcceptanceDate: "",
  buyerInspection: "",
  recissionPeriod: "",
  rescissionDeadline: "",
  appraisal: "",
  appraisalComplete: "",
  preSignDate: "",
  finalWalkthrough: "",
  sellerPaidClosingCosts: "",
  // 3. title, both sides
  titleCompany: "",
  titleCloserName: "",
  titleCloserEmail: "",
  titleCloserPhone: "",
  otherTitleCompany: "",
  otherTitleCloserName: "",
  otherTitleCloserEmail: "",
  otherTitleCloserPhone: "",
  // 1. buildTurnInSeed.js step3
  agentCommission: 0,
  thirdpartyagentCommission: 0,
  fixedagentcommission: false,
  brokeradminfee: 0, // stays 0 for the commission math; deal-summary.jsx checks Number(...) > 0
  brokeradminfeeWaived: false,
  brokeradminfeeWaivedAt: null,
  brokeradminfeeWaivedBy: "",
  brokeradminfeeBeforeWaive: "",
  // 1. buildTurnInSeed.js step4
  flatfee: false,
  flatfeeamount: 0,
  buyersidecommissionrate: "",
  sellersidecommissionrate: "",
  teamleadcommissionrate: "",
  agentCommissionamount: 0,
  teamleaddiscount: 0,
  agentdiscount: 0,
  leadsourceCommissionamount: 0,
  teamleadCommissionamount: 0,
  totalCommissionamount: 0,
  bankcommission: 0,
  thirdpartyagentcommissionamount: 0,
  takehomecommissionamount: 0,
  // 4. spec
  accountingStatus: DELETE_FIELD,
  accountingReviewedBy: DELETE_FIELD,
  accountingReviewedAt: DELETE_FIELD,
  accountingFlagNote: DELETE_FIELD,
  accountingFlaggedBy: DELETE_FIELD,
  accountingFlaggedAt: DELETE_FIELD,
  requestType: "",
  approvedby: "",
  awaitingClientCare: false,
  underContractDate: null,
};

const EMPTY_MAP_FIELDS = ["acceptanceEmails", "transactionEmails"];

// ---- Small helpers ------------------------------------------------------------

const hasOwn = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);
const isPlainObject = (value) => Object.prototype.toString.call(value) === "[object Object]";
const asArray = (value) => (Array.isArray(value) ? value : []);
const asPlainObject = (value) => (isPlainObject(value) ? value : {});
const stampTime = (now) => (Number.isFinite(now) ? now : Date.now());

// A field's exact previous value, or MISSING when the deal never had one, so a
// restore removes the field again instead of inventing a value.
const savedValueOf = (deal, field) =>
  hasOwn(deal, field) && deal[field] !== undefined ? deal[field] : MISSING;

const personStamp = (actor) => ({
  id: actor && actor.id != null ? String(actor.id) : "",
  name: actor && actor.name != null ? String(actor.name) : "",
  role: actor && actor.role != null ? String(actor.role) : "",
});

// Only the known reason chips, once each, in the dialog's order.
const cleanReasons = (reasons) => {
  const picked = asArray(reasons);
  return FELL_THROUGH_REASONS.filter((reason) => picked.includes(reason));
};

const isContractTask = (task) => !!task && CONTRACT_TASK_STAGES.includes(task.stage);
const entryTypeName = (entry) => (entry && typeof entry === "object" ? entry.type : "");
const AGENT_ROLE_RE = /agent/i;
const CO_ROLE_RE = /^\s*co\b/i; // "Co-Buyers Agent", "Co Buyer's Agent"; not "Coordinator"

// Split contactType into the buyer's side (removed) and everyone else (kept).
// A contact id leaves contactSyncId only when no kept entry still uses it.
const splitBuyerSidePeople = (deal, isBuyerSidePerson) => {
  const removed = [];
  const kept = [];
  asArray(deal.contactType).forEach((entry) => {
    if (isBuyerSidePerson(entryTypeName(entry))) removed.push(entry);
    else kept.push(entry);
  });
  const idOf = (entry) => (entry && entry.contactId ? String(entry.contactId) : "");
  const stayingIds = new Set(kept.map(idOf).filter(Boolean));
  const leavingIds = new Set(removed.map(idOf).filter((id) => id && !stayingIds.has(id)));
  const syncIds = asArray(deal.contactSyncId);
  return {
    removed,
    kept,
    removedIds: syncIds.filter((id) => leavingIds.has(String(id))),
    keptIds: syncIds.filter((id) => !leavingIds.has(String(id))),
  };
};

// ---- Public rules ---------------------------------------------------------------

export function clearedValueFor(field) {
  if (EMPTY_MAP_FIELDS.includes(field)) return {};
  if (hasOwn(CLEARED_VALUES, field)) return CLEARED_VALUES[field];
  throw new Error(`clearedValueFor: "${field}" is not a contract field`);
}

// Text holding only a zero amount: "0", "0.00", "-0.00". Every Edit Deal save
// re-runs the commission math and stores each amount through .toFixed(2), so a
// cleared listing gets "0.00" with no new buyer entered (add-deal.jsx,
// handleNext step 4). A lone space or any other text is not a zero amount.
const ZERO_AMOUNT_TEXT_RE = /^\s*[-+]?(\d+\.?\d*|\.\d+)\s*$/;
const isZeroAmountText = (value) =>
  typeof value === "string" && ZERO_AMOUNT_TEXT_RE.test(value) && Number(value) === 0;

// Empty means: missing, null, "", 0, zero amount text ("0", "0.00"), a marker, {} or [].
export function isEmptyValue(value) {
  if (value === undefined || value === null) return true;
  if (value === "" || value === 0 || isZeroAmountText(value)) return true;
  if (value === MISSING || value === DELETE_FIELD) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
}

// "Cleared" is empty, or the false a fresh contract gives a yes/no flag
// (flatfee, fixedagentcommission, inhousereferral).
export function isClearedValue(value) {
  return value === false || isEmptyValue(value);
}

// A seller-side listing that is Under Contract or Pending (dealStage). A Sale
// Turn In still waiting for approval (status "Pending", a different field from
// the "Pending" stage) qualifies too (Henry, 09/14/2026): planFellThrough marks
// it "Approved", because once its contract is gone there is nothing left to
// approve, and that keeps it out of Client Care's File Requests queue
// (src/utils/fileRequestQueue.js). Restore puts "Pending" back.
export function isFellThroughEligible(deal) {
  if (!deal) return false;
  if (isBuyerSide(deal.dealtype)) return false;
  return FELL_THROUGH_FROM_STAGES.includes(deal.dealStage);
}

export function isMovingBack(deal, toStage) {
  return isFellThroughEligible(deal) && BACK_TO_STAGES.includes(toStage);
}

export function planFellThrough(
  deal,
  { stage, reasons = [], note = "", actor, now, isBuyerSideRole } = {},
) {
  if (!isFellThroughEligible(deal)) throw new Error("not-eligible");
  if (!BACK_TO_STAGES.includes(stage)) throw new Error("bad-stage");

  const createdAt = stampTime(now);
  const historyId = `ch_${createdAt}`;
  const isBuyerSidePerson = typeof isBuyerSideRole === "function" ? isBuyerSideRole : () => false;

  // 1. The exact previous value of every contract field.
  const values = {};
  CONTRACT_FIELDS.forEach((field) => {
    values[field] = hasOwn(deal, field) && deal[field] !== undefined ? deal[field] : MISSING;
  });

  // 2. Contract-stage tasks and their taskStatusTracker keys. While those keys
  //    exist, the next contract's checklist is never seeded.
  const tasks = asArray(deal.tasks);
  const tasksRemoved = tasks.filter(isContractTask);
  const tasksKept = tasks.filter((task) => !isContractTask(task));
  const tracker = asPlainObject(deal.taskStatusTracker);
  const trackerRemoved = {};
  const trackerKept = {};
  Object.keys(tracker).forEach((key) => {
    if (tracker[key] === undefined) return;
    if (CONTRACT_TASK_STAGES.includes(key)) trackerRemoved[key] = tracker[key];
    else trackerKept[key] = tracker[key];
  });

  // 3. The buyer's side people.
  const people = splitBuyerSidePeople(deal, isBuyerSidePerson);
  // The row names the main buyer's agent: a "Co-" agent (Co-Buyers Agent) only
  // when no other named agent came off.
  const namedAgents = people.removed.filter(
    (entry) => entry && AGENT_ROLE_RE.test(String(entryTypeName(entry) || "")) && entry.fullName,
  );
  const buyerAgent =
    namedAgents.find((entry) => !CO_ROLE_RE.test(String(entryTypeName(entry) || ""))) ||
    namedAgents[0];

  const historyDoc = {
    id: historyId,
    dealId: String(deal.createdat ?? deal.id ?? ""),
    createdAt,
    createdBy: personStamp(actor),
    reasons: cleanReasons(reasons),
    note: String(note ?? "").trim(),
    stageBefore: deal.dealStage,
    stageAfter: stage,
    statusBefore: hasOwn(deal, "status") && deal.status !== undefined ? deal.status : MISSING,
    // The fall-through always puts the listing back on the market below, so these
    // two remember what it was before: a listing that was archived, or cancelled,
    // must go back to hidden and cancelled when the contract is restored.
    archivedBefore: savedValueOf(deal, "archived"),
    cancelledAtBefore: savedValueOf(deal, "cancelledAt"),
    values,
    tasksRemoved,
    trackerRemoved,
    contactsRemoved: {
      contactType: people.removed,
      contactSyncId: people.removedIds,
    },
    summary: {
      salesPrice: isEmptyValue(deal.offerPrice) ? "" : deal.offerPrice,
      closingDate: isEmptyValue(deal.closingdate) ? "" : deal.closingdate,
      buyerAgentName: buyerAgent ? String(buyerAgent.fullName) : "",
    },
    restoredAt: null,
    restoredBy: null,
  };

  // 4. The deal: contract fields back to a fresh contract, listing back on the market.
  const dealPatch = {};
  CONTRACT_FIELDS.forEach((field) => {
    dealPatch[field] = clearedValueFor(field);
  });
  dealPatch.dealStage = stage;
  // Listing Prep: the day the listing came back to this stage, dated from the same `now`.
  Object.assign(dealPatch, stageEntryPatch(deal, deal.dealStage, stage, prepTodayYMD(new Date(createdAt))));
  dealPatch.cancelledAt = null;
  dealPatch.archived = false;
  dealPatch.tasks = tasksKept;
  dealPatch.taskStatusTracker = trackerKept;
  dealPatch.contactType = people.kept;
  dealPatch.contactSyncId = people.keptIds;
  // An unapproved Sale Turn In (Henry, 09/14/2026): with its contract gone there is
  // nothing left to approve. "Approved" keeps it out of the File Requests queue,
  // which lists status "Pending" deals whose requestType isn't "saleTurnIn".
  if (deal.status === "Pending") dealPatch.status = "Approved";

  return { historyId, historyDoc, dealPatch };
}

// ---- Restore This Contract ------------------------------------------------------

// A copy of the given maps merged left to right, without undefined values.
const mergeDefined = (...maps) => {
  const out = {};
  maps.forEach((map) => {
    const source = asPlainObject(map);
    Object.keys(source).forEach((key) => {
      if (source[key] !== undefined) out[key] = source[key];
    });
  });
  return out;
};

// Henry's own commission and attribution settings (Henry, 09/15/2026). A split
// percentage, the split type, or a team lead / agent attribution field says how
// the house and the agents divide a commission, not who is buying, so typing one
// on a cleared listing must never grey out Restore This Contract.
// Deliberately NOT here, so each still blocks a restore: every commission AMOUNT
// (totalCommissionamount, agentCommissionamount, teamleadCommissionamount,
// leadsourceCommissionamount, bankcommission, takehomecommissionamount,
// thirdpartyagentcommissionamount), a dollar figure only a contract produces.
// The flat fee, the broker admin fee and the buyer and seller side rates moved
// to FEE_AND_RATE_FIELDS below (Henry, 09/16/2026) and no longer block.
export const SPLIT_AND_ATTRIBUTION_FIELDS = [
  "agentCommission", // "Agent Split", a percentage
  "fixedagentcommission", // the split type: a fixed cut instead of a percentage
  "teamleadcommissionrate", // "Team Lead Rate"
  "teamleaddiscount",
  "agentdiscount",
  "assignedteamlead",
  "teamleadName",
  "inhousereferral", // whose deal it is (see deal credit attribution)
  "thirdpartyagentName",
  "thirdpartyagentCommission", // "Third-Party Agent Split", a percentage
];

// Henry's own fee and rate settings (Henry, 09/16/2026). He sets these on the
// listing, so typing one on a cleared listing is not a newer buyer's contract and
// must not grey out Restore This Contract. A real commission AMOUNT still does.
export const FEE_AND_RATE_FIELDS = [
  "flatfee", // "Flat Fee", on or off
  "flatfeeamount",
  "brokeradminfee", // the brokerage's own admin fee
  "brokeradminfeeWaived", // and its Waived switch, with the switch's stamps
  "brokeradminfeeWaivedAt",
  "brokeradminfeeWaivedBy",
  "brokeradminfeeBeforeWaive",
  "buyersidecommissionrate", // "Buyer Side Rate", a percentage
  "sellersidecommissionrate", // "Seller Side Rate", a percentage
];

// Saved fields that everyday work changes without a newer buyer: Edit Deal and
// board edits re-stamp approvedby, and sending any transaction email writes
// transactionEmails. Restore ignores them (Henry, 09/14/2026), along with the
// commission and attribution settings above and the fee and rate settings.
// awaitingClientCare only says Client Care has a file to look at, never who is
// buying (Henry, 2026-09-19); a real new turn-in still blocks through requestType.
export const RESTORE_CHECK_SKIP = [
  "approvedby",
  "transactionEmails",
  "awaitingClientCare",
  ...SPLIT_AND_ATTRIBUTION_FIELDS,
  ...FEE_AND_RATE_FIELDS,
];

const hasOpenContractTask = (deal) =>
  asArray(deal.tasks).some((task) => !!task && FELL_THROUGH_FROM_STAGES.includes(task.stage));

// Restore is allowed only when no newer buyer has been entered (spec, "Restore
// This Contract"). A missing history item reports "already-restored" and a
// missing deal reports "wrong-side": neither can be restored. A listing in
// Under Contract or Pending reports "under-contract"; a Closed one reports
// "closed", so a sold house gets a sentence that fits it.
export function canRestore(deal, historyDoc) {
  if (!historyDoc || historyDoc.restoredAt) return { ok: false, reason: "already-restored" };
  if (!deal || isBuyerSide(deal.dealtype)) return { ok: false, reason: "wrong-side" };
  if (deal.dealStage === "Closed") return { ok: false, reason: "closed" };
  if (CONTRACT_TASK_STAGES.includes(deal.dealStage)) return { ok: false, reason: "under-contract" };
  const savedFields = Object.keys(asPlainObject(historyDoc.values)).filter(
    (field) => !RESTORE_CHECK_SKIP.includes(field),
  );
  const newerValue = savedFields.some((field) => !isClearedValue(deal[field]));
  if (newerValue || hasOpenContractTask(deal)) return { ok: false, reason: "newer-contract" };
  return { ok: true, reason: "" };
}

// Identity keys that stop a restore from adding the same thing twice. null means
// no usable identity, so the item is always added.
const taskKey = (task) =>
  task && task.id !== undefined && task.id !== null && task.id !== ""
    ? `${task.stage}|${String(task.id)}`
    : null;

const contactKey = (entry) =>
  entry && typeof entry === "object"
    ? [entry.typeId, entry.contactId, entry.fullName].map((v) => (v == null ? "" : String(v))).join("|")
    : null;

const syncIdKey = (id) => (id === undefined || id === null || id === "" ? null : String(id));

const appendMissing = (current, additions, keyOf) => {
  const seen = new Set(current.map(keyOf).filter((key) => key !== null));
  const out = [...current];
  additions.forEach((item) => {
    const key = keyOf(item);
    if (key !== null && seen.has(key)) return;
    if (key !== null) seen.add(key);
    out.push(item);
  });
  return out;
};

// The Email Center's marks ("sent", "skipped") made after the fall-through stay:
// they go on top of the saved map, so an email already sent to the client while
// the listing was back on the market never shows as unsent and goes out twice.
// With no marks made since, the saved value comes back exactly.
const EMAIL_MARK_KEYS = ["sent", "skipped"];

const restoredTransactionEmails = (saved, current) => {
  const now = asPlainObject(current);
  const hasNewMarks = EMAIL_MARK_KEYS.some((key) => Object.keys(mergeDefined(now[key])).length > 0);
  if (!hasNewMarks) return saved === MISSING || saved === undefined ? DELETE_FIELD : saved;
  const base = saved === MISSING ? {} : asPlainObject(saved);
  const out = mergeDefined(base);
  EMAIL_MARK_KEYS.forEach((key) => {
    const merged = mergeDefined(base[key], now[key]);
    if (Object.keys(merged).length > 0) out[key] = merged;
  });
  return out;
};

export function planRestore(deal, historyDoc, { actor, now } = {}) {
  const check = canRestore(deal, historyDoc);
  if (!check.ok) return { ok: false, reason: check.reason, dealPatch: null, historyPatch: null };

  const dealPatch = {};
  const values = asPlainObject(historyDoc.values);
  Object.keys(values).forEach((field) => {
    const saved = values[field];
    dealPatch[field] = saved === MISSING || saved === undefined ? DELETE_FIELD : saved;
  });
  if (hasOwn(values, "transactionEmails")) {
    dealPatch.transactionEmails = restoredTransactionEmails(values.transactionEmails, deal.transactionEmails);
  }

  const removedPeople = asPlainObject(historyDoc.contactsRemoved);
  dealPatch.tasks = appendMissing(asArray(deal.tasks), asArray(historyDoc.tasksRemoved), taskKey);
  dealPatch.taskStatusTracker = mergeDefined(deal.taskStatusTracker, historyDoc.trackerRemoved);
  dealPatch.contactType = appendMissing(
    asArray(deal.contactType),
    asArray(removedPeople.contactType),
    contactKey,
  );
  dealPatch.contactSyncId = appendMissing(
    asArray(deal.contactSyncId),
    asArray(removedPeople.contactSyncId).filter((id) => syncIdKey(id) !== null),
    syncIdKey,
  );
  dealPatch.dealStage = FELL_THROUGH_FROM_STAGES.includes(historyDoc.stageBefore)
    ? historyDoc.stageBefore
    : "Under Contract";
  // Listing Prep: the day the contract came back, dated from the same `now`.
  Object.assign(dealPatch, stageEntryPatch(deal, deal.dealStage, dealPatch.dealStage, prepTodayYMD(new Date(stampTime(now)))));
  // Back to what the listing was before the contract fell through. MISSING covers
  // both a listing that never had the field and a contract saved before these two
  // were recorded (09/16/2026): those restore as they always did, on the market
  // and un-cancelled.
  const cancelledBefore = savedValueOf(historyDoc, "cancelledAtBefore");
  dealPatch.cancelledAt = cancelledBefore === MISSING ? null : cancelledBefore;
  const archivedBefore = savedValueOf(historyDoc, "archivedBefore");
  if (archivedBefore !== MISSING) dealPatch.archived = archivedBefore;
  if (historyDoc.statusBefore === "Pending") dealPatch.status = "Pending";

  return {
    ok: true,
    reason: "",
    dealPatch,
    historyPatch: { restoredAt: stampTime(now), restoredBy: personStamp(actor) },
  };
}
